import React, { useEffect, useState } from "react";
import moment from "moment";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Container,
  Grid,
  Typography,
  Button,
  Link,
  TextField,
  ButtonBase,
} from "@mui/material";
import ExternalLink from "../utilities/ExternalLink";
import ExternalButtonBase from "../utilities/ExternalButtonBase";

import {
  BCPSearch,
  BCPCircleArrowUp,
  BCPAngleDown,
  BCPXmark,
  BCPFilter,
  BCPAngleUp,
} from "../icons/bcp/BCPIcons";

import { handlePagingAll } from "../api/APIUtilities";
import { postNotification } from "../utilities/Notifications";
import { cleanEventLocation } from "../utilities/UtilityFunctions";

import { Formik } from "formik";
import GenericPicker from "../utilities/GenericPicker";
import GoogleAddressSearch from "../utilities/GoogleAddressSearch";
import { getEnvironmentVariable } from "../../global/environmentVariables";

const printEventDate = (d1, d2) => {
  if (moment(d1).format("M") === moment(d2).format("M")) {
    // same month
    if (moment(d1).format("D") === moment(d2).format("D")) {
      // same day
      return moment(d1).format("Do MMM");
    } else {
      return moment(d1).format("Do") + "-" + moment(d2).format("Do MMM");
    }
  } else {
    return moment(d1).format("Do MMM") + "-" + moment(d2).format("Do MMM");
  }
};

const BadgeEventSize = ({ event, sx, circuit }) => {
  var numRounds = event?.numberOfRounds || 0;
  if (event?.numPlayers > 0) {
    if (circuit && circuit?.itc) {
      if (
        circuit?.hasOwnProperty("SuperMajorEventSize") &&
        event?.numPlayers >= circuit?.SuperMajorEventSize &&
        numRounds >= 7
      ) {
        return <Chip sx={sx} label="SUPER" size="small" color="error" />;
      } else if (
        circuit?.hasOwnProperty("MajorEventSize") &&
        event?.numPlayers >= circuit?.MajorEventSize &&
        numRounds >= 5
      ) {
        return <Chip sx={sx} label="MAJOR" size="small" color="error" />;
      } else if (
        circuit?.hasOwnProperty("GTEventSize") &&
        event?.numPlayers >= circuit?.GTEventSize &&
        numRounds >= 5
      ) {
        return <Chip sx={sx} label="GT" size="small" color="error" />;
      }
    }
  }

  return <></>;
};

const BadgePlayerSize = ({ event, sx }) => {
  return (
    <Chip
      sx={sx}
      label={`${event?.numPlayers} Players`}
      size="small"
      color="warning"
    />
  );
};

const BadgeCircuitOwner = ({ event, sx, circuit, organization }) => {
  if (
    event?.gameStoreId &&
    (event?.gameStoreId === circuit?.gameStoreId ||
      event?.gameStoreId === organization?.id)
  ) {
    return (
      <Chip
        sx={sx}
        label={`${event?.gameStoreName} Event`}
        size="small"
        color="info"
      />
    );
  }
  return <></>;
};

const BadgeEventStatus = ({ event, sx }) => {
  return (
    <Chip
      sx={sx}
      label={
        event?.submitted
          ? "Submitted"
          : event?.ended
            ? "Ended"
            : event?.started
              ? // && event?.currentRound > 0
                "Started"
              : "Not Started"
      }
      size="small"
      color={
        event?.submitted || event?.ended
          ? "success"
          : event?.started
            ? // && event?.currentRound > 0
              "secondary"
            : "info"
      }
    />
  );
};

export default function CalendarList(props) {
  const { circuit, organization } = props;
  const [events, setEvents] = useState(null);
  const [months, setMonths] = useState(null);

  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [searchReady, setSearchReady] = useState(false);

  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [filters, setFilters] = useState({});
  const [expandAll, setExpandAll] = useState(false);

  const [searchValues, setSearchValues] = useState({
    location: "Current Location",
    selectedAddress: "",
    selectedLocation: null,
    distance: "",
    minPlayers: "",
  });
  const [geoLocation, setGeoLocation] = useState(null);
  const [locationOpen, setLocationOpen] = useState(false);

  const updateMonths = () => {
    var newMonths = months || {};

    var startDate;
    var endDate;

    if (circuit) {
      if (circuit?.startDate) {
        startDate = moment(circuit.startDate);
      }

      if (circuit?.endDate) {
        endDate = moment(circuit.endDate);
      }
    }
    if (!startDate) startDate = moment();
    if (!endDate) endDate = moment();

    startDate = startDate.endOf("month");
    endDate = endDate.endOf("month");

    while (startDate.isBefore(endDate)) {
      if (!newMonths[startDate.format("YYYY-MM-DD")]) {
        newMonths[startDate.format("YYYY-MM-DD")] = [];
      }
      startDate = startDate.add(1, "month").endOf("month");
    }

    setMonths(newMonths);
  };

  const handleScroll = () => {
    if (window.pageYOffset > 150) {
      if (!showScrollToTop) setShowScrollToTop(true);
    } else {
      if (showScrollToTop) setShowScrollToTop(false);
    }
  };
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  });

  useEffect(() => {
    const go = async () => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(function (position) {
          setGeoLocation(position.coords);
          setSearchReady(true);
        });
      } else {
        setSearchValues({
          ...searchValues,
          location: "Global",
        });
        setSearchReady(true);
      }
      if (circuit || organization) {
        updateMonths();
        var queryStringParameters = {
          startDate: circuit?.startDate
            ? moment(circuit?.startDate).format("YYYY-MM-DD")
            : moment().startOf("month").format("YYYY-MM-DD"),
          endDate: circuit?.endDate
            ? moment(circuit?.endDate).format("YYYY-MM-DD")
            : moment().add(3, "months").endOf("month").format("YYYY-MM-DD"),
        };
        if (circuit) {
          queryStringParameters.leagueId = circuit?.id;
        }
        if (organization) {
          queryStringParameters.gameStoreId = organization.id;
        }
        try {
          await handlePagingAll("/events", queryStringParameters, setEvents);
          setLoading(false);
        } catch (err) {
          postNotification("error", err);
        }
      }
    };
    go();
    // eslint-disable-next-line
  }, [circuit, organization]);

  useEffect(() => {
    var newMonths = { ...months };
    Object.keys(newMonths).forEach((key) => (newMonths[key] = []));

    if (events?.length > 0) {
      events.forEach((event) => {
        var month = moment(event?.eventDate)
          .endOf("month")
          .format("YYYY-MM-DD");
        if (!newMonths[month]) {
          newMonths[month] = [];
        }
        // event?.numPlayers =
        //   event?.started && event?.currentRound > 0
        //     ? event?.checkedInPlayers > 0
        //       ? event?.checkedInPlayers
        //       : event?.totalPlayers
        //     : event?.numTickets > 0
        //     ? event?.numTickets
        //     : event?.totalPlayers;
        event.numPlayers = event.started
          ? event?.checkedInPlayers > 0
            ? event?.checkedInPlayers
            : event?.totalPlayers
          : event?.numTickets > 0
            ? event?.numTickets
            : event?.totalPlayers;
        newMonths[month].push(event);
      });
    }

    setMonths(newMonths);
    // eslint-disable-next-line
  }, [events]);

  return (
    <Box sx={{ width: "100%" }}>
      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid container sx={{ gap: 2 }}>
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary",
              textAlign: "center",
            }}
          >
            <Typography variant="h3" gutterBottom>
              {circuit ? circuit?.name : ""}
            </Typography>
            <Typography variant="body1" gutterBottom>
              Powered by{" "}
              <ExternalLink
                target="_blank"
                href={`${window.location.protocol}//${window.location.host}`}
                underline="none"
              >
                {getEnvironmentVariable("COMPANY_NAME")}
              </ExternalLink>
            </Typography>
          </Grid>
          {!loading && events?.length === 0 && (
            <Grid
              item
              xs={12}
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: 1,
                boxShadow: 1,
                backgroundColor: "background.secondary",
                textAlign: "center",
              }}
            >
              <Typography variant="h3" gutterBottom>
                There are currently no events
                {circuit
                  ? " for this Circuit"
                  : organization
                    ? " for this Organization"
                    : ""}
              </Typography>
            </Grid>
          )}
          {events?.length > 0 && (
            <>
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 1,
                  boxShadow: 1,
                  backgroundColor: "background.secondary",
                }}
              >
                <Formik
                  initialValues={searchValues}
                  enableReinitialize={true}
                  onSubmit={async (
                    values,
                    { setSubmitting, setFieldValue }
                  ) => {
                    var newFilters = {};
                    if (values.minPlayers > 0) {
                      newFilters.minPlayers = values.minPlayers;
                    }
                    if (values.distance > 0) {
                      if (values.location === "Current Location") {
                        newFilters.distance = values.distance;
                        newFilters.center = {
                          latitude: geoLocation.latitude,
                          longitude: geoLocation.longitude,
                        };
                      } else if (values.selectedLocation?.coordinate) {
                        newFilters.distance = values.distance;
                        newFilters.center = {
                          latitude: values.selectedLocation.coordinate[1],
                          longitude: values.selectedLocation.coordinate[0],
                        };
                      }
                    }

                    setFilters(newFilters);
                  }}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    touched,
                    values,
                    setFieldValue,
                    submitForm,
                  }) => (
                    <form onSubmit={handleSubmit}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Button
                            color={"primary"}
                            variant={"contained"}
                            fullWidth
                            startIcon={<BCPFilter />}
                            onClick={() => {
                              setShowSearch(!showSearch);
                            }}
                            disabled={!searchReady}
                          >
                            Filters
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Button
                            color={"primary"}
                            variant={"contained"}
                            fullWidth
                            endIcon={
                              expandAll ? <BCPAngleUp /> : <BCPAngleDown />
                            }
                            onClick={() => {
                              setExpandAll(!expandAll);
                            }}
                          >
                            {!expandAll ? "Expand All" : "Collapse All"}
                          </Button>
                        </Grid>
                      </Grid>
                      {showSearch && (
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          <Grid item xs={12}>
                            <GenericPicker
                              label="Location"
                              inclAny={false}
                              name="location"
                              list={[
                                <Box sx={{ p: 1 }}>
                                  <GoogleAddressSearch
                                    setLocationData={(e) => {
                                      setFieldValue(
                                        "selectedAddress",
                                        e?.formatted_address
                                          ? e.formatted_address
                                          : ""
                                      );
                                      setFieldValue(
                                        "location",
                                        e?.formatted_address
                                          ? e.formatted_address
                                          : "Current Location"
                                      );
                                      setFieldValue("selectedLocation", e);
                                      setLocationOpen(false);
                                    }}
                                  />
                                </Box>,
                                values.selectedAddress !== ""
                                  ? values.selectedAddress
                                  : null,
                                geoLocation ? "Current Location" : null,
                                "Global",
                                "Online Only",
                              ]}
                              value={values.location}
                              handleChange={handleChange}
                              open={locationOpen}
                              setOpen={setLocationOpen}
                            />
                          </Grid>
                          {values.location !== "Global" &&
                            values.location !== "Online Only" && (
                              <Grid item xs={12}>
                                <TextField
                                  name="distance"
                                  label="Distance (in miles)"
                                  value={values.distance}
                                  variant="outlined"
                                  fullWidth
                                  margin="normal"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  error={Boolean(
                                    touched.distance && errors.distance
                                  )}
                                  helperText={
                                    touched.distance && errors.distance
                                  }
                                  type="number"
                                  inputProps={{
                                    min: 0,
                                  }}
                                />
                              </Grid>
                            )}

                          <Grid item xs={12}>
                            <TextField
                              name="minPlayers"
                              label="Minimum No. Players"
                              value={values.minPlayers}
                              variant="outlined"
                              fullWidth
                              margin="normal"
                              type="number"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.minPlayers && errors.minPlayers
                              )}
                              helperText={
                                touched.minPlayers && errors.minPlayers
                              }
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <Button
                              color={"error"}
                              variant={"outlined"}
                              fullWidth
                              startIcon={<BCPXmark />}
                              // sx={{ height: "56px" }}
                              onClick={(e) => {
                                setFieldValue("distance", "");
                                setFieldValue("minPlayers", "");
                                handleSubmit(e);
                              }}
                            >
                              Clear
                            </Button>
                          </Grid>
                          <Grid item xs={6}>
                            <Button
                              type="submit"
                              color={"primary"}
                              variant={"contained"}
                              fullWidth
                              startIcon={<BCPSearch />}
                              // sx={{ height: "56px" }}
                            >
                              Search
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </form>
                  )}
                </Formik>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  p: 2,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 1,
                  boxShadow: 1,
                  backgroundColor: "background.secondary",
                }}
              >
                <Grid container spacing={1}>
                  {months &&
                    Object.keys(months).map((key) => {
                      // const monthEvents = months[key].filter(function (el) {
                      //   if (!filters) return true;
                      //   if (
                      //     filters.minPlayers &&
                      //     el.numPlayers < filters.minPlayers
                      //   ) {
                      //     return false;
                      //   }
                      //   return true;
                      // });
                      return (
                        <Grid key={key} item xs={12}>
                          <CalendarMonth
                            index={key}
                            month={months[key]}
                            filters={filters}
                            circuit={circuit}
                            organization={organization}
                            expandAll={expandAll}
                          />
                          {/* <Accordion
                          // expanded={expandAll} onChange={handleChange('panel1')}
                          >
                            <AccordionSummary
                              expandIcon={<BCPAngleDown />}
                              aria-controls={`month-${key}-content`}
                              id={`month-${key}-header`}
                              sx={{
                                backgroundColor: "primary.main",
                                color: "primary.contrastText",
                              }}
                            >
                              <Typography>
                                {moment(key).format("MMMM YYYY")}{" "}
                                <Chip
                                  label={`${
                                    Object.keys(filters).length > 0
                                      ? `${monthEvents.length} / ${months[key].length}`
                                      : months[key].length
                                  } Events`}
                                  size="small"
                                  //color="info"
                                  sx={{
                                    color: "#fff",
                                    backgroundColor: "text.secondary",
                                  }}
                                />
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={1}>
                                {monthEvents.map((event, index) => {
                                  const isOwnerEvent =
                                    event.gameStoreId &&
                                    (event.gameStoreId ===
                                      circuit?.gameStoreId ||
                                      event.gameStoreId === organization?.id);
                                  return (
                                    <Grid key={index} item xs={12}>
                                      <ButtonBase
                                        href={`/event/${event.id}`}
                                        target="_blank"
                                        sx={{
                                          width: "100%",
                                          textAlign: "left",
                                          justifyContent: "start",
                                          display: "block",
                                          borderBottom:
                                            "1px solid rgba(0, 0, 0, 0.12)",
                                          pb: 1,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: isOwnerEvent
                                              ? "120%"
                                              : "80%",
                                            fontWeight: isOwnerEvent
                                              ? "500"
                                              : "400",
                                            textAlign: "left",
                                          }}
                                        >
                                          <span
                                            style={{ fontWeight: "bolder" }}
                                          >
                                            {printEventDate(
                                              event.eventDate,
                                              event.eventEndDate
                                            )}
                                          </span>{" "}
                                          {event.name},{" "}
                                          {cleanEventLocation(event)}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "80%",
                                            fontWeight: "400",
                                            textAlign: "left",
                                          }}
                                        >
                                          <BadgeCircuitOwner
                                            event={event}
                                            sx={{ mr: 1 }}
                                            circuit={circuit}
                                            organization={organization}
                                          />
                                          <BadgeEventSize
                                            event={event}
                                            sx={{ mr: 1 }}
                                            circuit={circuit}
                                          />
                                          <BadgePlayerSize
                                            event={event}
                                            sx={{ mr: 1 }}
                                          />
                                          <BadgeEventStatus
                                            event={event}
                                            sx={{ mr: 1 }}
                                          />
                                        </Typography>
                                      </ButtonBase>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </AccordionDetails>
                          </Accordion> */}
                        </Grid>
                      );
                    })}
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Container>
      {showScrollToTop && (
        <Button
          variant="contained"
          color="info"
          size="large"
          onClick={() => {
            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
          }}
          sx={{
            position: "fixed",
            bottom: "40px",
            right: "10px",
            minWidth: "unset",
            borderRadius: "50%",
            padding: "unset",
          }}
        >
          <BCPCircleArrowUp style={{ "font-size": "2.5rem" }} />
        </Button>
      )}
    </Box>
  );
}

const CalendarMonth = (props) => {
  const { index, month, filters, circuit, organization, expandAll } = props;

  const [expanded, setExpanded] = useState(false);

  const monthEvents = month.filter(function (el) {
    if (!filters) return true;
    if (filters.minPlayers && el.numPlayers < filters.minPlayers) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    setExpanded(expandAll);
  }, [expandAll]);

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary
        expandIcon={<BCPAngleDown />}
        aria-controls={`month-${index}-content`}
        id={`month-${index}-header`}
        sx={{
          backgroundColor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        <Typography>
          {moment(index).format("MMMM YYYY")}{" "}
          <Chip
            label={`${
              Object.keys(filters).length > 0
                ? `${monthEvents.length} / ${month.length}`
                : month.length
            } Events`}
            size="small"
            //color="info"
            sx={{
              color: "#fff",
              backgroundColor: "text.secondary",
            }}
          />
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          {monthEvents.map((event, index) => {
            const isOwnerEvent =
              event?.gameStoreId &&
              (event?.gameStoreId === circuit?.gameStoreId ||
                event?.gameStoreId === organization?.id);
            return (
              <Grid key={index} item xs={12}>
                <ExternalButtonBase
                  href={`/event/${event?.id}`}
                  target="_blank"
                  sx={{
                    width: "100%",
                    textAlign: "left",
                    justifyContent: "start",
                    display: "block",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    pb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: isOwnerEvent ? "120%" : "80%",
                      fontWeight: isOwnerEvent ? "500" : "400",
                      textAlign: "left",
                    }}
                  >
                    <span style={{ fontWeight: "bolder" }}>
                      {printEventDate(event?.eventDate, event?.eventEndDate)}
                    </span>{" "}
                    {event?.name}, {cleanEventLocation(event)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "80%",
                      fontWeight: "400",
                      textAlign: "left",
                    }}
                  >
                    <BadgeCircuitOwner
                      event={event}
                      sx={{ mr: 1 }}
                      circuit={circuit}
                      organization={organization}
                    />
                    <BadgeEventSize
                      event={event}
                      sx={{ mr: 1 }}
                      circuit={circuit}
                    />
                    <BadgePlayerSize event={event} sx={{ mr: 1 }} />
                    <BadgeEventStatus event={event} sx={{ mr: 1 }} />
                  </Typography>
                </ExternalButtonBase>
              </Grid>
            );
          })}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
